// React.
import { useContext, useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from "highcharts-react-official";
import api from "../../../api/Base";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import { useLicences } from "../../../context/LicenceContext";

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';
import { TransactionContentionType } from '../../../types/instance/TransactionContentionType';

// Components.
import TransactionContentionTable from '../../../component/instance/table/TransactionsContentionTable';

// Helper.
import {
    fetchResults,
    getConvertedTimeToUTC,
    highchartsCredits
} from '../../../helpers/utils';
import TransactionApi from '../../../api/instance/TransactionApi';
import ConditionalRender from "../../../helpers/ConditionalRender";
import Helper from "../../../helpers/Helper";
import { chartOptionsGranularity } from '../../../helpers/chartOptionsIncreasedGranularity';
import { showError } from '../../../utils/showAlert';
import TabDisabledBanner from '../../../component/instance/TabDisabledBanner';

// Constants
import { CHART_DATA_TYPE } from '../../../component/Constants';

export default function TransactionContention(props: {
    instance: InstanceTarget,
    instanceTypes: any[],
}) {
    const { instance, instanceTypes } = props;
    const timeRangeContext = useContext(TimeRangeContext);

    const [transactions, setTransactions] = useState<TransactionContentionType[]>([]);
    const [timeSeries, setTimeSeries] = useState<number[][]>([]);

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { licences } = useLicences();

    const timePeak = useMemo(() => {
        // @ts-ignore
        return Helper.getPeakValue(timeSeries.map(i => i[1]))
    }, [timeSeries])


    useEffect(() => {
        async function load() {
            setLoading(true);
            setError(null);

            // Fire both requests in parallel so one failing doesn't block the other
            const tableRequest = fetchResults(
                [
                    TransactionApi.fetchContention(buildApiQuery(timeRangeContext.timeRange), instance.id)
                ]
            );
            const chartRequest = api.get(`transaction/contention/time?${buildApiQuery(timeRangeContext.timeRange)}&id=${instance.id}&limit=${process.env.REACT_APP_API_LIMIT}`);

            const [tableResult, chartResult] = await Promise.allSettled([tableRequest, chartRequest]);

            if (!active) return;

            // Handle table data — independent of chart
            if (tableResult.status === 'fulfilled') {
                setTransactions(tableResult.value[0] || []);
            } else {
                const errorMessage = tableResult.reason?.message || String(tableResult.reason);
                console.error('Failed to load transaction contention table.', errorMessage);
                setError(errorMessage);
                showError('Failed to load transaction contention', errorMessage);
                setTransactions([]);
            }

            // Handle chart data — independent of table
            if (chartResult.status === 'fulfilled') {
                const transactionsData: number[][] = (chartResult.value.data || []).map((item: any) => [getConvertedTimeToUTC(item), item.contentionduration]);
                setTimeSeries(transactionsData);
            } else {
                console.error('Failed to load transaction contention chart.', chartResult.reason?.message || chartResult.reason);
                setTimeSeries([]);
            }

            setLoading(false);
        }

        let active: boolean = true;
        void load();
        return () => {
            // Clean up afterwards to avoid race conditions.
            active = false;
        }
    }, [instance.id, timeRangeContext]);

    const timeChart = useMemo(() => {
        const timeChartOptions = chartOptionsGranularity([
            {
                type: 'column', data: timeSeries, name: 'Contention Duration', zIndex: 1, tooltip: { valueSuffix: 'ms', }
            },
        ], timePeak, {
            credits: { enabled: highchartsCredits(licences) },
            legend: true,
            timeRangeContext: timeRangeContext,
            tooltip: {
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                },
            },
            yAxis: [{
                title: {
                    text: 'Duration (ms)'
                },
                labels: {
                    formatter: function (): string {
                        // @ts-ignore
                        return Helper.getTimeInEnglish(this?.value);
                    }
                },
                min: 0,
                max: timePeak
            }]
        })
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts}
                                options={timeChartOptions}/>;

    }, [timeSeries, timePeak, timeRangeContext, licences]);

    return (<>
        <TabDisabledBanner instance={instance} instanceTypes={instanceTypes} collectFlag={!!instance.collecttransactioncontention} defaultCollectKey="defaultcollecttransactioncontention" featureName="Transaction Contention" />
        <div className="row">
            <div className="col">
                <div className="card collapsible">
                    <div className="card-header">
                        <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                        Time spent
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                           data-bs-target="#collapseExecutions" aria-expanded="false"
                           aria-controls="collapseExecutions"/>
                    </div>
                    <ConditionalRender if={loading}>
                        <div className="w-100 text-center text-muted mt-3">
                            <div className="loader spinner chartSpinner">
                            </div>
                            <p className="mt-3">
                                Loading data...
                            </p>
                        </div>
                    </ConditionalRender>

                    <ConditionalRender if={!loading}>
                        <div id="collapseExecutions" className="card-body collapse show">
                            {timeChart}
                        </div>
                    </ConditionalRender>
                </div>
            </div>
        </div>

        <div className="row row-cols-1">
            <div id="space" className="tab-pane fade show active" role="tabpanel" aria-labelledby="space-tab">
                <TransactionContentionTable error={error} loading={loading}
                                            data={transactions} instance={instance}/>
            </div>
        </div>
    </>)
}
